import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  useDeleteConceptGroupMutation,
  useGetConceptGroupUsageQuery,
} from '@verantos-dev/data-access-concept-groups';
import type { ListResponse } from '@verantos-dev/types-common';

import { projectPageKeys } from '../lib/api/queryKeys';
import type { ProjectArtifactEntry } from '../lib/api/types';

interface UseDeleteConceptGroupDialogArgs {
  projectId: string;
  conceptGroupId: string;
  onClose: () => void;
}

/**
 * Behaviour for the inline concept-group delete confirmation on the artifacts
 * page — mirrors `useDeleteAnalyticalDatasetDialog`, but the delete itself
 * goes through the RTK Query `data-access-concept-groups` slice (the same
 * one the org-wide library uses) rather than a TanStack mutation, since
 * that's the existing concept-group data-access layer. The project-artifacts
 * manifest is a separate (TanStack) cache, so a successful delete manually
 * drops the row and invalidates the manifest query — RTK's tag invalidation
 * doesn't reach it.
 */
export const useDeleteConceptGroupDialog = ({
  projectId,
  conceptGroupId,
  onClose,
}: UseDeleteConceptGroupDialogArgs) => {
  const queryClient = useQueryClient();

  const { data: usageData, isLoading: checkingUsage } = useGetConceptGroupUsageQuery({
    conceptGroupId,
  });
  const [deleteMutation, { isLoading: deleting, error: deleteError }] =
    useDeleteConceptGroupMutation();

  const isInUse = usageData?.data?.isUsed ?? false;

  const onSubmit = useCallback(async () => {
    if (isInUse) return;
    const result = await deleteMutation({ conceptGroupId });
    if ('error' in result) return;

    const manifestKey = projectPageKeys.artifactsForProject(projectId);
    queryClient.setQueryData<ListResponse<ProjectArtifactEntry>>(manifestKey, (old) =>
      old ? { ...old, data: old.data.filter((entry) => entry.id !== conceptGroupId) } : old
    );
    queryClient.invalidateQueries({ queryKey: manifestKey });
  }, [isInUse, deleteMutation, conceptGroupId, queryClient, projectId]);

  return {
    onSubmit,
    closeDialog: onClose,
    isInUse,
    isSubmitting: deleting,
    isInitialLoading: checkingUsage,
    submittingError: deleteError,
    isValidForSubmit: !checkingUsage && !deleting && !isInUse,
  };
};
